import { useMemo, useState, useContext, useCallback, createContext } from 'react';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
// assets
import { countries } from 'src/assets/data';
// components
import Iconify from 'src/components/iconify';
// api
import { useGetGroups, useGetSectors } from 'src/api/ransomware';
// types
import { IAnalyticsFilterParams } from 'src/types/ransomware';
//
import { countryFlag } from './utils';

// ----------------------------------------------------------------------
// The GLOBAL analytics scope. The audit's critical finding was that filter/
// entity context was lost on every navigation because state lived in the
// current page's URL. It now lives in a section-level React context, persisted
// to sessionStorage, so "Country = India, last 30 days" survives every hop
// across the ransomware module until explicitly cleared. Every /analytics
// endpoint accepts the same params, so `params` drops into any hook as-is.

const DAY_MS = 86_400_000;
const STORAGE_KEY = 'ransomware.scope.v1';

export type AnalyticsRange = '30d' | '90d' | '180d' | 'all';

const RANGE_OPTIONS: { value: AnalyticsRange; label: string }[] = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '180d', label: 'Last 6 months' },
  { value: 'all', label: 'Last 12 months' },
];

const RANGE_DAYS: Record<string, number> = { '30d': 30, '90d': 90, '180d': 180 };

type ScopeState = { group: string; country: string; sector: string; range: AnalyticsRange };

const DEFAULT_SCOPE: ScopeState = { group: '', country: '', sector: '', range: 'all' };

export type AnalyticsFilters = {
  group: string;
  country: string;
  sector: string;
  range: AnalyticsRange;
  /** ready to hand to any useGetAnalytics* hook */
  params: IAnalyticsFilterParams;
  isActive: boolean;
  setFilter: (name: 'group' | 'country' | 'sector' | 'range', value: string) => void;
  reset: () => void;
};

// ---- context provider (mounted once around the whole ransomware module) ----

type ScopeContextValue = {
  scope: ScopeState;
  setScope: (next: ScopeState) => void;
};

const ScopeContext = createContext<ScopeContextValue | null>(null);

function hydrate(): ScopeState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SCOPE;
    const parsed = JSON.parse(raw);
    return {
      group: typeof parsed.group === 'string' ? parsed.group : '',
      country: typeof parsed.country === 'string' ? parsed.country : '',
      sector: typeof parsed.sector === 'string' ? parsed.sector : '',
      range: RANGE_DAYS[parsed.range] || parsed.range === 'all' ? parsed.range : 'all',
    };
  } catch {
    return DEFAULT_SCOPE;
  }
}

export function RansomwareScopeProvider({ children }: { children: React.ReactNode }) {
  const [scope, setScopeState] = useState<ScopeState>(hydrate);

  const setScope = useCallback((next: ScopeState) => {
    setScopeState(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage disabled — scope still works for the session in memory */
    }
  }, []);

  const value = useMemo(() => ({ scope, setScope }), [scope, setScope]);
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useAnalyticsFilters(): AnalyticsFilters {
  const ctx = useContext(ScopeContext);
  // fallback to a local (non-persistent) scope if a page renders outside the
  // provider — keeps the hook safe, though the provider should always be present
  const [localScope, setLocalScope] = useState<ScopeState>(DEFAULT_SCOPE);
  const scope = ctx?.scope ?? localScope;
  const setScope = ctx?.setScope ?? setLocalScope;

  const { group, country, sector, range } = scope;

  const setFilter = useCallback(
    (name: 'group' | 'country' | 'sector' | 'range', value: string) => {
      setScope({ ...scope, [name]: name === 'range' ? (value as AnalyticsRange) : value });
    },
    [scope, setScope]
  );

  const reset = useCallback(() => setScope(DEFAULT_SCOPE), [setScope]);

  const params = useMemo<IAnalyticsFilterParams>(() => {
    const days = RANGE_DAYS[range];
    return {
      group: group || undefined,
      country: country || undefined,
      sector: sector || undefined,
      from: days ? new Date(Date.now() - days * DAY_MS).toISOString().slice(0, 10) : undefined,
    };
  }, [group, country, sector, range]);

  return {
    group,
    country,
    sector,
    range,
    params,
    isActive: Boolean(group || country || sector || RANGE_DAYS[range]),
    setFilter,
    reset,
  };
}

// ----------------------------------------------------------------------

// real countries only (the data file has a leading empty-code entry)
const countryOptions = countries.filter((c) => c.code);

type Props = {
  filters: AnalyticsFilters;
  /** hide selectors that a page already scopes (e.g. sector page hides sector) */
  hide?: ('group' | 'country' | 'sector')[];
};

export default function AnalyticsFilterBar({ filters, hide = [] }: Props) {
  const { groups } = useGetGroups();
  const { sectors } = useGetSectors();

  const groupOptions = useMemo(() => groups.map((g) => g.group), [groups]);
  const sectorOptions = useMemo(() => sectors.map((s) => s.sector), [sectors]);

  const hidden = (name: 'group' | 'country' | 'sector') => hide.indexOf(name) !== -1;

  return (
    <Stack
      spacing={2}
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'stretch', md: 'center' }}
      sx={{ mb: 3 }}
    >
      {!hidden('group') && (
        <Autocomplete
          freeSolo
          options={groupOptions}
          value={filters.group || null}
          onChange={(_e, value) => filters.setFilter('group', value || '')}
          sx={{ width: { xs: 1, md: 220 } }}
          renderInput={(params) => <TextField {...params} label="Threat actor" />}
        />
      )}

      {!hidden('country') && (
        <Autocomplete
          options={countryOptions}
          value={countryOptions.find((c) => c.code === filters.country) || null}
          onChange={(_e, option) => filters.setFilter('country', option?.code || '')}
          getOptionLabel={(option) => `${option.label} (${option.code})`}
          isOptionEqualToValue={(option, val) => option.code === val.code}
          autoHighlight
          sx={{ width: { xs: 1, md: 220 } }}
          renderOption={(props, option) => (
            <Box component="li" sx={{ '& > span': { mr: 1, fontSize: 18 } }} {...props}>
              <span>{countryFlag(option.code)}</span>
              {option.label} ({option.code})
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Country" />}
        />
      )}

      {!hidden('sector') && (
        <TextField
          select
          label="Sector"
          value={filters.sector}
          onChange={(e) => filters.setFilter('sector', e.target.value)}
          sx={{ width: { xs: 1, md: 200 } }}
        >
          <MenuItem value="">All sectors</MenuItem>
          {sectorOptions.map((sector) => (
            <MenuItem key={sector} value={sector}>
              {sector}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        label="Time range"
        value={filters.range}
        onChange={(e) => filters.setFilter('range', e.target.value)}
        sx={{ width: { xs: 1, md: 170 } }}
      >
        {RANGE_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>

      {filters.isActive && (
        <Button
          color="inherit"
          onClick={filters.reset}
          startIcon={<Iconify icon="eva:close-fill" />}
          sx={{ flexShrink: 0 }}
        >
          Clear
        </Button>
      )}
    </Stack>
  );
}
